import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

/**
 * SEOHead Component
 * 
 * Manages all SEO-related meta tags, Open Graph tags, and structured data.
 * Automatically adapts to current language (EN/ES).
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - SEO keywords (comma-separated)
 * @param {string} props.ogImage - Open Graph image URL (optional)
 * @param {string} props.ogType - Open Graph type (default: 'website')
 * @param {Object} props.structuredData - JSON-LD structured data (optional)
 * 
 * @example
 * <SEOHead 
 *   title="CNC Machining Services"
 *   description="Precision CNC machining in G10, FR4..."
 *   keywords="CNC machining, G10, FR4"
 *   ogImage="/images/cnc-og.jpg"
 * />
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords = '',
  ogImage = '/images/logo/dsi-logo.png',
  ogType = 'website',
  structuredData = null,
  canonicalUrl = ''
}) => {
  const { language } = useLanguage();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Construct full title
  const fullTitle = `${title} | DSI Durango`;
  
  // Full image URL for Open Graph
  const fullOgImage = ogImage.startsWith('http') 
    ? ogImage 
    : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph Tags (LinkedIn, Facebook) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="DSI Durango" />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="DSI Durango" />
      
      {/* Mobile */}
      <meta name="theme-color" content="#000000" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      
      {/* Hreflang Tags for Multi-language */}
      <link rel="alternate" hreflang="en" href={`${baseUrl}${window.location.pathname}`} />
      <link rel="alternate" hreflang="es" href={`${baseUrl}${window.location.pathname}`} />
      <link rel="alternate" hreflang="es-MX" href={`${baseUrl}${window.location.pathname}`} />
      <link rel="alternate" hreflang="x-default" href={`${baseUrl}${window.location.pathname}`} />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
